import { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardBody, Button, Switch, Badge } from '../components/ui';
import { HiDatabase, HiChartBar, HiExclamationCircle, HiRefresh } from '../components/icons';
import { useSelectedTenant } from '../stores/tenantStore';
import { showToast } from '../utils/toast';
import {
  replicationConfigService,
  normalizeClioReplicationSchedulerConfig,
  CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS,
  CLIO_REPLICATION_REPORT_KINDS,
  type ClioReplicationSchedulerConfig,
  type ClioReplicationSchedulerEntity,
  type ClioReplicationReportKind,
  type ReplicateToDWConditionType,
  type ClioReplicationSchedulerEntityConfig,
} from '../services/replication-config.service';

const DEFAULT_DW_WINDOW_TYPE: ReplicateToDWConditionType = 'rollingMonthsWindow';
const DEFAULT_DW_ROLLING_MONTHS = 12;

export function DataReplicationConfigurationPage() {
  const { selectedTenant } = useSelectedTenant();
  const [rawConfig, setRawConfig] = useState<Partial<ClioReplicationSchedulerConfig> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadConfig = async (tenantId: string) => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await replicationConfigService.getTenantConfig(tenantId);
      const schedulerConfig =
        data?.modules?.clioReplicationScheduler ||
        data?.modules?.['clio-replication-scheduler'];
      setRawConfig(schedulerConfig || null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTenant) {
      loadConfig(selectedTenant.id);
    } else {
      setRawConfig(null);
      setLoadError(null);
    }
  }, [selectedTenant]);

  if (!selectedTenant) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white mb-2">Data Replication Configuration</h1>
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300 mb-8">
          Configure Clio to DynamoDB replication and DW/Redshift extraction settings per tenant.
        </p>
        <Card padding="lg" variant="outlined">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <HiExclamationCircle className="h-12 w-12 text-xtnd-dark-300 dark:text-xtnd-dark-500 mb-4" />
            <p className="text-lg font-medium text-xtnd-dark-700 dark:text-xtnd-dark-200">
              No tenant selected
            </p>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-1">
              Select a tenant from the dropdown in the navigation bar to manage its replication configuration.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white mb-2">Data Replication Configuration</h1>
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300 mb-8">
          Loading configuration for <span className="font-semibold">{selectedTenant.name}</span>...
        </p>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {[0, 1].map(i => (
            <Card key={i} padding="lg" variant="outlined">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-xtnd-dark-100 dark:bg-xtnd-dark-700 rounded w-2/3" />
                <div className="h-4 bg-xtnd-dark-100 dark:bg-xtnd-dark-700 rounded w-1/2" />
                <div className="space-y-3">
                  {[0, 1, 2, 3].map(j => (
                    <div key={j} className="h-16 bg-xtnd-dark-100 dark:bg-xtnd-dark-700 rounded" />
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white mb-2">Data Replication Configuration</h1>
        <Card padding="lg" variant="outlined">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <HiExclamationCircle className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-lg font-medium text-xtnd-dark-700 dark:text-xtnd-dark-200">
              Failed to load configuration
            </p>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-1 mb-4">{loadError}</p>
            <Button variant="outline" size="sm" onClick={() => loadConfig(selectedTenant.id)} leftIcon={<HiRefresh className="h-4 w-4" />}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white">Data Replication Configuration</h1>
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300 mt-1">
          Managing replication for <span className="font-semibold text-xtnd-dark-800 dark:text-white">{selectedTenant.name}</span>
          <span className="ml-2 text-xs font-mono text-xtnd-dark-400 dark:text-xtnd-dark-400">({selectedTenant.id})</span>
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ClioToDynamoCard
          tenantId={selectedTenant.id}
          config={rawConfig}
          onSaved={() => loadConfig(selectedTenant.id)}
        />
        <DWConfigCard
          tenantId={selectedTenant.id}
          config={rawConfig}
          onSaved={() => loadConfig(selectedTenant.id)}
        />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────
// Clio to DynamoDB Card
// ──────────────────────────────────────────────────

function ClioToDynamoCard({
  tenantId,
  config,
  onSaved,
}: {
  tenantId: string;
  config: Partial<ClioReplicationSchedulerConfig> | null;
  onSaved: () => void;
}) {
  const normalizedInitialConfig = useMemo(() => normalizeClioReplicationSchedulerConfig(config), [config]);
  const [draftConfig, setDraftConfig] = useState<ClioReplicationSchedulerConfig>(normalizedInitialConfig);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraftConfig(normalizedInitialConfig);
  }, [normalizedInitialConfig]);

  const isDirty = useMemo(
    () => JSON.stringify(draftConfig) !== JSON.stringify(normalizedInitialConfig),
    [draftConfig, normalizedInitialConfig]
  );

  const updateEntity = (entity: ClioReplicationSchedulerEntity, enabled: boolean) => {
    setDraftConfig(prev => ({
      ...prev,
      entities: {
        ...prev.entities,
        [entity]: { ...prev.entities[entity], replicateToClio: enabled },
      },
    }));
  };

  const handleSave = async () => {
    if (!isDirty || isSaving) return;
    setIsSaving(true);
    try {
      await replicationConfigService.updateTenantReplicationConfig(tenantId, draftConfig);
      showToast.success('Clio to DynamoDB configuration saved');
      onSaved();
    } catch {
      showToast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card padding="lg" variant="outlined">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <HiDatabase className="h-5 w-5 text-blue-700 dark:text-blue-300" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-xtnd-dark-800 dark:text-white">
                Clio API to DynamoDB
              </h3>
              <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                Manage tenant and entity toggles for Clio replication into DynamoDB.
              </p>
            </div>
          </div>
          <Badge variant={draftConfig.enabled ? 'success' : 'warning'} size="sm">
            {draftConfig.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
      </CardHeader>

      <CardBody className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-xtnd-dark-200 dark:border-xtnd-dark-600 p-4">
          <div>
            <p className="text-sm font-semibold text-xtnd-dark-800 dark:text-white">Tenant Replication Toggle</p>
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">Master circuit breaker for Clio API ingestion.</p>
          </div>
          <Switch
            checked={draftConfig.enabled}
            onChange={checked => setDraftConfig(prev => ({ ...prev, enabled: checked }))}
            disabled={isSaving}
            aria-label="Toggle tenant replication"
          />
        </div>

        <div className={`space-y-2 ${!draftConfig.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
          {CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS.map(entity => (
            <div
              key={entity.key}
              className="flex items-start justify-between rounded-lg border border-xtnd-dark-200 dark:border-xtnd-dark-600 p-3"
            >
              <div className="pr-4">
                <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">{entity.label}</p>
                <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">{entity.description}</p>
              </div>
              <Switch
                checked={draftConfig.entities[entity.key]?.replicateToClio !== false}
                onChange={checked => updateEntity(entity.key, checked)}
                disabled={isSaving || !draftConfig.enabled}
                aria-label={`Toggle ${entity.label} Clio replication`}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={!isDirty || isSaving} loading={isSaving}>
            Save Clio to DynamoDB Configuration
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// ──────────────────────────────────────────────────
// DW / Redshift Configuration Card
// ──────────────────────────────────────────────────

function DWConfigCard({
  tenantId,
  config,
  onSaved,
}: {
  tenantId: string;
  config: Partial<ClioReplicationSchedulerConfig> | null;
  onSaved: () => void;
}) {
  const normalizedInitialConfig = useMemo(() => normalizeClioReplicationSchedulerConfig(config), [config]);
  const [draftConfig, setDraftConfig] = useState<ClioReplicationSchedulerConfig>(normalizedInitialConfig);
  const [isSaving, setIsSaving] = useState(false);
  const [isOperationRunning, setIsOperationRunning] = useState(false);
  const [confirmDrop, setConfirmDrop] = useState<{ type: 'entity'; entityKey: string } | { type: 'report'; reportKind: string } | null>(null);
  const [selectedMonths, setSelectedMonths] = useState<Record<string, string>>({});

  useEffect(() => {
    setDraftConfig(normalizedInitialConfig);
    setConfirmDrop(null);
  }, [normalizedInitialConfig]);

  const isDirty = useMemo(
    () => JSON.stringify(draftConfig) !== JSON.stringify(normalizedInitialConfig),
    [draftConfig, normalizedInitialConfig]
  );

  const rollingMonthOptions = useMemo(() => {
    const months: Array<{ value: string; label: string }> = [];
    const now = new Date();
    const startMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const n = normalizedInitialConfig.reportReplication.backfillDefaultPeriods ?? 12;
    for (let i = 0; i < n; i++) {
      const d = new Date(startMonth.getFullYear(), startMonth.getMonth() - i, 1);
      const yyyy = String(d.getFullYear());
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      months.push({ value: `${yyyy}${mm}`, label: `${yyyy}-${mm}` });
    }
    return months;
  }, [normalizedInitialConfig.reportReplication.backfillDefaultPeriods]);

  const updateDwEntity = (entity: ClioReplicationSchedulerEntity, enabled: boolean) => {
    setDraftConfig(prev => ({
      ...prev,
      entities: {
        ...prev.entities,
        [entity]: {
          ...prev.entities[entity],
          replicateToDW: enabled,
          ...(enabled ? buildDefaultDwWindow(prev.entities[entity]) : {}),
        },
      },
    }));
  };

  const updateDwEntityWindowType = (entity: ClioReplicationSchedulerEntity, conditionType: ReplicateToDWConditionType) => {
    setDraftConfig(prev => {
      const current = prev.entities[entity];
      const nextValue = conditionType === 'startDate'
        ? (typeof current?.replicateToDWValue === 'string' ? current.replicateToDWValue : rollingMonthsAgoToDate(DEFAULT_DW_ROLLING_MONTHS))
        : (typeof current?.replicateToDWValue === 'number' ? current.replicateToDWValue : DEFAULT_DW_ROLLING_MONTHS);
      return {
        ...prev,
        entities: {
          ...prev.entities,
          [entity]: { ...current, replicateToDWConditionType: conditionType, replicateToDWValue: nextValue },
        },
      };
    });
  };

  const updateDwEntityWindowValue = (entity: ClioReplicationSchedulerEntity, value: string | number) => {
    setDraftConfig(prev => ({
      ...prev,
      entities: { ...prev.entities, [entity]: { ...prev.entities[entity], replicateToDWValue: value } },
    }));
  };

  const updateReportReplication = (patch: Partial<ClioReplicationSchedulerConfig['reportReplication']>) => {
    setDraftConfig(prev => ({ ...prev, reportReplication: { ...prev.reportReplication, ...patch } }));
  };

  const updateReportEnabled = (reportKind: ClioReplicationReportKind, enabled: boolean) => {
    setDraftConfig(prev => ({
      ...prev,
      reportReplication: { ...prev.reportReplication, reports: { ...prev.reportReplication.reports, [reportKind]: enabled } },
    }));
  };

  const updateReportExportByMonth = (reportKind: ClioReplicationReportKind, byMonth: boolean) => {
    setDraftConfig(prev => ({
      ...prev,
      reportReplication: { ...prev.reportReplication, reportExportByMonth: { ...prev.reportReplication.reportExportByMonth, [reportKind]: byMonth } },
    }));
  };

  const handleSave = async () => {
    if (!isDirty || isSaving) return;
    setIsSaving(true);
    try {
      await replicationConfigService.updateTenantReplicationConfig(tenantId, draftConfig);
      showToast.success('DW configuration saved');
      onSaved();
    } catch {
      showToast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDropEntityTables = async (entityKey: string) => {
    setIsOperationRunning(true);
    try {
      await replicationConfigService.dropEntityTables(tenantId, entityKey);
      showToast.success(`Tables dropped for ${entityKey}`);
      setConfirmDrop(null);
    } catch {
      showToast.error('Failed to drop tables');
    } finally {
      setIsOperationRunning(false);
    }
  };

  const handleDropReportTable = async (reportKind: string) => {
    setIsOperationRunning(true);
    try {
      await replicationConfigService.dropReportTable(tenantId, reportKind);
      showToast.success(`Report table dropped for ${reportKind}`);
      setConfirmDrop(null);
    } catch {
      showToast.error('Failed to drop report table');
    } finally {
      setIsOperationRunning(false);
    }
  };

  const handleResyncMonth = async (reportKind: string, monthCode: string) => {
    setIsOperationRunning(true);
    try {
      await replicationConfigService.resyncReportMonth(tenantId, reportKind, monthCode);
      showToast.success(`Resync initiated for ${reportKind} (${monthCode})`);
    } catch {
      showToast.error('Failed to resync month');
    } finally {
      setIsOperationRunning(false);
    }
  };

  const handleResyncSnapshot = async (reportKind: string) => {
    setIsOperationRunning(true);
    try {
      await replicationConfigService.resyncSnapshotReport(tenantId, reportKind);
      showToast.success(`Snapshot resync initiated for ${reportKind}`);
    } catch {
      showToast.error('Failed to resync snapshot');
    } finally {
      setIsOperationRunning(false);
    }
  };

  const handleInvokeDwSync = async (type: 'all' | 'entities' | 'reports' = 'all') => {
    setIsOperationRunning(true);
    try {
      await replicationConfigService.invokeDwSync(tenantId, type);
      showToast.success(`DW sync (${type}) invoked`);
    } catch {
      showToast.error('Failed to invoke DW sync');
    } finally {
      setIsOperationRunning(false);
    }
  };

  const disabled = isSaving;

  return (
    <Card padding="lg" variant="outlined">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <HiChartBar className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-xtnd-dark-800 dark:text-white">
                DW and Redshift Configuration
              </h3>
              <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                Configure DW extraction, Redshift enablement, and report periods.
              </p>
            </div>
          </div>
          <Badge variant={draftConfig.reportReplication.enabled ? 'success' : 'warning'} size="sm">
            {draftConfig.reportReplication.enabled ? 'Reports Enabled' : 'Reports Disabled'}
          </Badge>
        </div>
      </CardHeader>

      <CardBody className="space-y-4">
        {/* Entity DW Toggles */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-xtnd-dark-800 dark:text-white">XTND to Data Warehouse</p>
          {CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS.map(entity => {
            const entityConfig = draftConfig.entities[entity.key];
            const savedEntityConfig = normalizedInitialConfig.entities[entity.key];
            const dwWindowType: ReplicateToDWConditionType = entityConfig?.replicateToDWConditionType === 'startDate' ? 'startDate' : DEFAULT_DW_WINDOW_TYPE;
            const isSavedDisabled = savedEntityConfig?.replicateToDW === false;
            const showDropButton = isSavedDisabled && !isDirty;
            const isConfirmingDrop = confirmDrop?.type === 'entity' && confirmDrop.entityKey === entity.key;

            return (
              <div key={entity.key} className="space-y-2 rounded-lg border border-xtnd-dark-200 dark:border-xtnd-dark-600 p-3">
                <div className="flex items-start justify-between">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">{entity.label}</p>
                    <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">Enable for DW/Redshift replication.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {showDropButton && !isConfirmingDrop && (
                      <Button variant="destructive" size="sm" onClick={() => setConfirmDrop({ type: 'entity', entityKey: entity.key })} disabled={isOperationRunning}>
                        Drop Table
                      </Button>
                    )}
                    <Switch
                      checked={entityConfig?.replicateToDW === true}
                      onChange={checked => updateDwEntity(entity.key, checked)}
                      disabled={disabled}
                      aria-label={`Toggle ${entity.label} DW replication`}
                    />
                  </div>
                </div>

                {isConfirmingDrop && (
                  <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 p-3 space-y-2">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      This will permanently drop all Redshift tables for this entity. This cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <Button variant="destructive" size="sm" loading={isOperationRunning} onClick={() => handleDropEntityTables(entity.key)}>
                        Proceed
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setConfirmDrop(null)} disabled={isOperationRunning}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                <div className={`${entityConfig?.replicateToDW ? '' : 'opacity-50 pointer-events-none'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-300">Data Window Type</label>
                      <select
                        value={dwWindowType}
                        onChange={e => updateDwEntityWindowType(entity.key, e.target.value as ReplicateToDWConditionType)}
                        disabled={disabled || entityConfig?.replicateToDW !== true}
                        className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                      >
                        <option value="rollingMonthsWindow">Rolling Months Window</option>
                        <option value="startDate">Start Date</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-300">Data Window Value</label>
                      {dwWindowType === 'rollingMonthsWindow' ? (
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={toRollingMonthsValue(entityConfig?.replicateToDWValue)}
                          onChange={e => updateDwEntityWindowValue(entity.key, toRollingMonthsValue(e.target.value))}
                          disabled={disabled || entityConfig?.replicateToDW !== true}
                          className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                        />
                      ) : (
                        <input
                          type="date"
                          value={toDateInputValue(entityConfig?.replicateToDWValue)}
                          onChange={e => updateDwEntityWindowValue(entity.key, e.target.value)}
                          disabled={disabled || entityConfig?.replicateToDW !== true}
                          className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Report Replication Section */}
        <div className="space-y-3 rounded-lg border border-xtnd-dark-200 dark:border-xtnd-dark-600 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-xtnd-dark-800 dark:text-white">Clio Report to DW Extraction</p>
              <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">Master toggle for Clio reporting extracts into DW/Redshift.</p>
            </div>
            <Switch
              checked={draftConfig.reportReplication.enabled}
              onChange={checked => updateReportReplication({ enabled: checked })}
              disabled={disabled}
              aria-label="Toggle Clio report DW extraction"
            />
          </div>

          <div className={`space-y-3 ${!draftConfig.reportReplication.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {/* Period Model */}
            <div>
              <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-300">Period Model</label>
              <select
                value={draftConfig.reportReplication.periodModel}
                onChange={e => updateReportReplication({ periodModel: e.target.value as 'CALENDAR' | 'FISCAL' })}
                disabled={disabled || !draftConfig.reportReplication.enabled}
                className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
              >
                <option value="CALENDAR">Calendar Month</option>
                <option value="FISCAL">Fiscal 52/53 Week</option>
              </select>
            </div>

            {draftConfig.reportReplication.periodModel === 'FISCAL' && (
              <div>
                <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-300">Fiscal Pattern</label>
                <select
                  value={draftConfig.reportReplication.fiscalPattern}
                  onChange={e => updateReportReplication({ fiscalPattern: e.target.value as '4-4-5' | '4-5-4' | '5-4-4' })}
                  disabled={disabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                >
                  <option value="4-4-5">4-4-5</option>
                  <option value="4-5-4">4-5-4</option>
                  <option value="5-4-4">5-4-4</option>
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-300">Week Start Day</label>
                <select
                  value={draftConfig.reportReplication.weekStartDay}
                  onChange={e => updateReportReplication({ weekStartDay: e.target.value as 'MONDAY' | 'SUNDAY' })}
                  disabled={disabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                >
                  <option value="MONDAY">Monday</option>
                  <option value="SUNDAY">Sunday</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-300">Fiscal Year Anchor Date</label>
                <input
                  type="date"
                  value={draftConfig.reportReplication.fiscalYearAnchorDate}
                  onChange={e => updateReportReplication({ fiscalYearAnchorDate: e.target.value })}
                  disabled={disabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-300">Week 53 Handling</label>
                <select
                  value={draftConfig.reportReplication.week53Handling}
                  onChange={e => updateReportReplication({ week53Handling: e.target.value as 'P12' | 'P11' | 'nearest-end' })}
                  disabled={disabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                >
                  <option value="P12">Period 12</option>
                  <option value="P11">Period 11</option>
                  <option value="nearest-end">Nearest End</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-300">Default Backfill Periods</label>
                <select
                  value={draftConfig.reportReplication.backfillDefaultPeriods}
                  onChange={e => updateReportReplication({ backfillDefaultPeriods: Number(e.target.value) as 12 | 24 | 36 })}
                  disabled={disabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={36}>36</option>
                </select>
              </div>
            </div>

            {/* Finalize Closed Periods */}
            <div className="flex items-center justify-between rounded-lg border border-xtnd-dark-200 dark:border-xtnd-dark-600 p-3">
              <div>
                <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">Finalize Closed Periods</p>
                <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">Enables close-period FULL snapshots at period boundaries.</p>
              </div>
              <Switch
                checked={draftConfig.reportReplication.finalizeClosedPeriods}
                onChange={checked => updateReportReplication({ finalizeClosedPeriods: checked })}
                disabled={disabled}
                aria-label="Toggle closed period finalization"
              />
            </div>

            {/* Report Toggles */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">Replicated Reports</p>
              {CLIO_REPLICATION_REPORT_KINDS.map(reportKind => {
                const isSavedReportDisabled = normalizedInitialConfig.reportReplication.reports[reportKind] === false;
                const showReportDropButton = isSavedReportDisabled && !isDirty;
                const isConfirmingReportDrop = confirmDrop?.type === 'report' && confirmDrop.reportKind === reportKind;
                const selectedMonth = selectedMonths[reportKind] ?? '';
                const isExportByMonth = draftConfig.reportReplication.reportExportByMonth?.[reportKind] !== false;

                return (
                  <div key={reportKind} className="rounded-lg border border-xtnd-dark-200 dark:border-xtnd-dark-600 p-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-xtnd-dark-700 dark:text-xtnd-dark-200">{reportKind.replace(/_/g, ' ')}</p>
                      <div className="flex items-center gap-2">
                        {showReportDropButton && !isConfirmingReportDrop && (
                          <Button variant="destructive" size="sm" onClick={() => setConfirmDrop({ type: 'report', reportKind })} disabled={isOperationRunning}>
                            Drop Table
                          </Button>
                        )}
                        <Switch
                          checked={draftConfig.reportReplication.reports[reportKind] !== false}
                          onChange={checked => updateReportEnabled(reportKind, checked)}
                          disabled={disabled}
                          aria-label={`Toggle ${reportKind} report`}
                        />
                      </div>
                    </div>

                    {isConfirmingReportDrop && (
                      <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 p-3 space-y-2">
                        <p className="text-sm font-medium text-red-800 dark:text-red-200">
                          This will permanently drop the <code className="font-mono">clio_report_{reportKind}</code> table.
                        </p>
                        <div className="flex gap-2">
                          <Button variant="destructive" size="sm" loading={isOperationRunning} onClick={() => handleDropReportTable(reportKind)}>Proceed</Button>
                          <Button variant="outline" size="sm" onClick={() => setConfirmDrop(null)} disabled={isOperationRunning}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {draftConfig.reportReplication.reports[reportKind] !== false && (
                      <div className="flex items-center justify-between pt-1">
                        <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">Export By Month</p>
                        <div className="flex items-center gap-2">
                          <Badge variant={isExportByMonth ? 'primary' : 'warning'} size="sm">
                            {isExportByMonth ? 'Monthly' : 'Snapshot'}
                          </Badge>
                          <Switch
                            checked={isExportByMonth}
                            onChange={checked => updateReportExportByMonth(reportKind, checked)}
                            disabled={disabled}
                            aria-label={`Toggle ${reportKind} export by month`}
                          />
                        </div>
                      </div>
                    )}

                    {normalizedInitialConfig.reportReplication.enabled && (
                      isExportByMonth ? (
                        <div className="flex items-center gap-2 pt-1">
                          <select
                            value={selectedMonth}
                            onChange={e => setSelectedMonths(prev => ({ ...prev, [reportKind]: e.target.value }))}
                            disabled={isOperationRunning}
                            className="flex-1 rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 px-2 py-1 text-xs text-xtnd-dark-800 dark:text-white"
                          >
                            <option value="">Select month to resync...</option>
                            {rollingMonthOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <Button size="sm" variant="outline" disabled={!selectedMonth || isOperationRunning} loading={isOperationRunning} onClick={() => handleResyncMonth(reportKind, selectedMonth)}>
                            Resync
                          </Button>
                        </div>
                      ) : (
                        <div className="pt-1">
                          <Button size="sm" variant="outline" disabled={isOperationRunning} loading={isOperationRunning} onClick={() => handleResyncSnapshot(reportKind)}>
                            Resync Snapshot
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => handleInvokeDwSync('all')} disabled={isDirty || isOperationRunning || disabled} loading={isOperationRunning}>
            Invoke DW Sync
          </Button>
          <Button onClick={handleSave} disabled={!isDirty || disabled} loading={isSaving}>
            Save DW and Redshift Configuration
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// ──────────────────────────────────────────────────
// Utility functions
// ──────────────────────────────────────────────────

function buildDefaultDwWindow(entityConfig?: ClioReplicationSchedulerEntityConfig) {
  if (entityConfig?.replicateToDWConditionType && entityConfig?.replicateToDWValue !== undefined) {
    return {};
  }
  return { replicateToDWConditionType: DEFAULT_DW_WINDOW_TYPE, replicateToDWValue: DEFAULT_DW_ROLLING_MONTHS };
}

function toRollingMonthsValue(value: unknown): number {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? parseInt(value, 10) : NaN;
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_DW_ROLLING_MONTHS;
  return Math.floor(parsed);
}

function toDateInputValue(value: unknown): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return rollingMonthsAgoToDate(DEFAULT_DW_ROLLING_MONTHS);
}

function rollingMonthsAgoToDate(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString().slice(0, 10);
}
